import { assertLabelPair, Size, t, toggleLabel, type ToggleLabelPair } from '@bms2/shared';
import { sizeClassMap } from '../classMaps';
import styles from './choice.module.css';

/**
 * Toggle Switch (piece 3): a native checkbox carrying role switch, with
 * appearance none and the track and thumb drawn over it. The track fills
 * status-good when On and status-inactive when Off, and a mandatory 13px
 * label to its right reads On or Off, updating with state, in the text token
 * and never colour-coded. No label is not a valid construction state.
 */
export interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** The pair to the right of the track; On/Off unless a caller supplies a more specific one. */
  labels?: ToggleLabelPair;
  /** What the switch controls, for the accessible name. */
  name: string;
  size?: Size;
  disabled?: boolean;
}

export const toggleSwitchDocumentedStates = ['off', 'on', 'small', 'large', 'disabled', 'yesNo'] as const;

const sizeClass = sizeClassMap({ small: styles.toggleSmall!, large: styles.toggleLarge! });

export function ToggleSwitch({ checked, onChange, labels, name, size = Size.Medium, disabled = false }: ToggleSwitchProps) {
  const pair = assertLabelPair(labels ?? { on: t('control.on'), off: t('control.off') });
  const label = toggleLabel(pair, checked);
  return (
    <label className={styles.row} data-size={size} data-toggle={checked ? 'on' : 'off'}>
      <input
        type="checkbox"
        role="switch"
        className={`${styles.toggle} ${sizeClass[size]}`}
        checked={checked}
        disabled={disabled}
        aria-label={name}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className={styles.toggleLabel} data-toggle-label="true" aria-hidden="true">
        {label}
      </span>
    </label>
  );
}
